import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useCQuery, invalidateQuery, FC } from "../../Services";
import { DataGrid } from "@mui/x-data-grid";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DateTime } from "luxon";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Autocomplete from "@mui/material/Autocomplete";
import Collapse from "@mui/material/Collapse";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import ArticleIcon from "@mui/icons-material/Article";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ComputerIcon from "@mui/icons-material/Computer";
import DnsIcon from "@mui/icons-material/Dns";
import HistoryIcon from "@mui/icons-material/History";
import ArchiveIcon from "@mui/icons-material/Archive";
import BackupIcon from "@mui/icons-material/Backup";
import DownloadIcon from "@mui/icons-material/Download";
import RestoreIcon from "@mui/icons-material/Restore";
import Checkbox from "@mui/material/Checkbox";
import { Link as RouterLink } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

const AUTO_REFRESH_INTERVAL = 30 * 1000;

const stringify = (val) => {
  if (typeof val === "string") return val;
  if (val == null) return "";
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
};

const formatTimestamp = (value) => {
  if (!value) return "—";
  const dt = DateTime.fromISO(value);
  if (dt.isValid) return dt.toFormat("dd/MM/yyyy HH:mm:ss");
  const dt2 = DateTime.fromJSDate(new Date(value));
  if (dt2.isValid) return dt2.toFormat("dd/MM/yyyy HH:mm:ss");
  return String(value);
};

const toQueryDate = (value) => {
  if (value == null) return null;
  if (typeof value === "string") return value;
  const dt = value?.isValid !== undefined ? value : DateTime.fromJSDate(value);
  return dt?.isValid ? dt.toISO() : null;
};

function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const SOURCE_COLORS = {
  client: { bg: "#e3f2fd", color: "#1565c0", label: "Client" },
  server: { bg: "#f3e5f5", color: "#7b1fa2", label: "Server" },
  unknown: { bg: "#f5f5f5", color: "#757575", label: "—" },
};

const LEVEL_COLORS = {
  info: { bg: "#e8f5e9", color: "#2e7d32", label: "info" },
  warn: { bg: "#fff3e0", color: "#e65100", label: "warn" },
  error: { bg: "#ffebee", color: "#c62828", label: "error" },
};

const SourceChip = ({ value }) => {
  const cfg = SOURCE_COLORS[value] || SOURCE_COLORS.unknown;
  return (
    <Chip
      label={cfg.label}
      size="small"
      icon={
        value === "client" ? (
          <ComputerIcon sx={{ fontSize: 14 }} />
        ) : value === "server" ? (
          <DnsIcon sx={{ fontSize: 14 }} />
        ) : undefined
      }
      sx={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: 11,
        height: 22,
        "& .MuiChip-icon": { color: cfg.color },
      }}
    />
  );
};

const LevelChip = ({ value }) => {
  const cfg = LEVEL_COLORS[value] || LEVEL_COLORS.info;
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: 11,
        height: 22,
        minWidth: 48,
        textTransform: "uppercase",
      }}
    />
  );
};

export const LogsPanel = () => {
  const [searchInput, setSearchInput] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [copiedRowId, setCopiedRowId] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [sourceFilter, setSourceFilter] = useState(null);
  const [levelFilter, setLevelFilter] = useState(null);
  const [clientFilter, setClientFilter] = useState(null);
  const [moduleFilter, setModuleFilter] = useState(null);

  const { data: clientsRaw = [] } = useCQuery("clients");
  const clientOptions = useMemo(() => {
    const list = Array.isArray(clientsRaw)
      ? clientsRaw
      : clientsRaw?.data || [];
    return list
      .map((c) => c._id)
      .filter(Boolean)
      .sort();
  }, [clientsRaw]);

  const debouncedSearch = useDebounce(searchInput);

  const prevSearchRef = useRef(debouncedSearch);
  const prevDateFromRef = useRef(dateFrom);
  const prevDateToRef = useRef(dateTo);

  useEffect(() => {
    const searchChanged = prevSearchRef.current !== debouncedSearch;
    const dateFromChanged = prevDateFromRef.current !== dateFrom;
    const dateToChanged = prevDateToRef.current !== dateTo;

    prevSearchRef.current = debouncedSearch;
    prevDateFromRef.current = dateFrom;
    prevDateToRef.current = dateTo;

    if (searchChanged || dateFromChanged || dateToChanged) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  }, [debouncedSearch, dateFrom, dateTo]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [sourceFilter, levelFilter, clientFilter, moduleFilter]);

  const queryParams = useMemo(() => {
    const params = {
      $limit: paginationModel.pageSize,
      $skip: paginationModel.page * paginationModel.pageSize,
      $sort: { serverTimestamp: -1 },
    };
    if (debouncedSearch) params.search = debouncedSearch;
    const from = toQueryDate(dateFrom);
    const to = toQueryDate(dateTo);
    if (from) params.dateFrom = from;
    if (to) params.dateTo = to;
    if (sourceFilter) params.source = sourceFilter;
    if (levelFilter) params.level = levelFilter;
    if (clientFilter) params.clientId = clientFilter;
    if (moduleFilter) params.module = moduleFilter;
    return params;
  }, [
    paginationModel,
    debouncedSearch,
    dateFrom,
    dateTo,
    sourceFilter,
    levelFilter,
    clientFilter,
    moduleFilter,
  ]);

  const {
    data: response,
    isLoading,
    isFetching,
  } = useCQuery(["logs", queryParams], [], {
    refetchInterval: autoRefresh ? AUTO_REFRESH_INTERVAL : false,
    placeholderData: (prev) => prev,
  });

  const rows = useMemo(() => {
    const list = response?.data || (Array.isArray(response) ? response : []);
    return list.map((log, idx) => ({
      ...log,
      _uid: log._id || `log-${idx}`,
    }));
  }, [response]);

  const rowCount = response?.total ?? rows.length;

  const handleRefresh = useCallback(() => {
    invalidateQuery("logs");
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchInput("");
    setDateFrom(null);
    setDateTo(null);
    setSourceFilter(null);
    setLevelFilter(null);
    setClientFilter(null);
    setModuleFilter(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleCopyMessage = useCallback((row) => {
    const text =
      typeof row.message === "object"
        ? JSON.stringify(row.message, null, 2)
        : stringify(row.message);
    navigator.clipboard.writeText(text);
    setCopiedRowId(row._uid);
    setTimeout(() => setCopiedRowId(null), 2000);
  }, []);

  const [archivesOpen, setArchivesOpen] = useState(false);
  const [selectedArchives, setSelectedArchives] = useState([]);
  const [restoreRunning, setRestoreRunning] = useState(false);
  const [backupRunning, setBackupRunning] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const {
    data: archivesRaw,
    isLoading: archivesLoading,
  } = useCQuery("logArchives", [], {
    enabled: archivesOpen,
  });
  const archives = useMemo(
    () => (Array.isArray(archivesRaw) ? archivesRaw : archivesRaw?.data || []),
    [archivesRaw]
  );

  const { data: batchesRaw } = useCQuery("restoreLogBatches", [], {
    enabled: archivesOpen,
  });
  const batches = useMemo(
    () => (Array.isArray(batchesRaw) ? batchesRaw : []),
    [batchesRaw]
  );
  const restoredFilenames = useMemo(() => {
    const set = new Set();
    batches.forEach((b) =>
      (b.sourceArchives || []).forEach((f) => set.add(f))
    );
    return set;
  }, [batches]);

  const [archivesPage, setArchivesPage] = useState(0);
  const [archivesRowsPerPage, setArchivesRowsPerPage] = useState(10);

  const pagedArchives = useMemo(() => {
    const start = archivesPage * archivesRowsPerPage;
    return archives.slice(start, start + archivesRowsPerPage);
  }, [archives, archivesPage, archivesRowsPerPage]);

  useEffect(() => {
    if (archives.length === 0) {
      setArchivesPage(0);
      return;
    }
    const lastPage = Math.max(0, Math.ceil(archives.length / archivesRowsPerPage) - 1);
    if (archivesPage > lastPage) setArchivesPage(lastPage);
  }, [archives.length, archivesRowsPerPage, archivesPage]);

  const toggleArchiveSelection = useCallback((filename) => {
    setSelectedArchives((prev) =>
      prev.includes(filename)
        ? prev.filter((f) => f !== filename)
        : [...prev, filename]
    );
  }, []);

  const handleRestoreArchives = useCallback(async () => {
    if (selectedArchives.length === 0) return;
    setRestoreRunning(true);
    try {
      const result = await FC.restoreLogsFromArchives(selectedArchives);
      setSnackbar({
        open: true,
        message: `Ripristinati ${result.totalInserted?.toLocaleString("it-IT") || 0} log. Consulta la pagina Log ripristinati.`,
        severity: "success",
      });
      setSelectedArchives([]);
      invalidateQuery("restoreLogBatches");
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Errore ripristino: " + (err?.message || "Errore sconosciuto"),
        severity: "error",
      });
    } finally {
      setRestoreRunning(false);
    }
  }, [selectedArchives]);

  const handleManualBackup = useCallback(async () => {
    setBackupRunning(true);
    try {
      await FC.service("log-archives").create({});
      setSnackbar({ open: true, message: "Backup completato con successo!", severity: "success" });
      invalidateQuery("logArchives");
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Errore durante il backup: " + (err?.message || "Errore sconosciuto"),
        severity: "error",
      });
    }
    setBackupRunning(false);
  }, []);

  const formatBytes = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const hasActiveFilters =
    searchInput ||
    dateFrom ||
    dateTo ||
    sourceFilter ||
    levelFilter ||
    clientFilter ||
    moduleFilter;

  const columns = [
    {
      field: "serverTimestamp",
      headerName: "Data / Ora",
      width: 170,
      sortable: false,
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ fontFamily: "monospace", fontSize: 12 }}
        >
          {formatTimestamp(params.value)}
        </Typography>
      ),
    },
    {
      field: "source",
      headerName: "Sorgente",
      width: 110,
      sortable: false,
      renderCell: (params) => <SourceChip value={params.value} />,
    },
    {
      field: "level",
      headerName: "Livello",
      width: 90,
      sortable: false,
      renderCell: (params) => <LevelChip value={params.value} />,
    },
    {
      field: "message",
      headerName: "Messaggio",
      flex: 1,
      minWidth: 260,
      sortable: false,
      valueGetter: (value) => stringify(value),
      renderCell: (params) => (
        <Tooltip title={params.value || ""} placement="top-start">
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              fontSize: 12,
              cursor: "default",
            }}
          >
            {params.value || "—"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "monitoredPath",
      headerName: "Percorso monitorato",
      width: 220,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => {
        const v = params.value;
        if (!v) {
          return (
            <Typography variant="body2" color="text.disabled" fontSize={12}>
              —
            </Typography>
          );
        }
        return (
          <Tooltip title={v} placement="top-start">
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontFamily: "monospace",
                fontSize: 11,
                maxWidth: "100%",
              }}
            >
              {v}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "clientId",
      headerName: "Client ID",
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontFamily: "monospace",
            color: "text.secondary",
            fontSize: 12,
          }}
        >
          {params.value || "—"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 55,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const isCopied = copiedRowId === params.row._uid;
        return (
          <Tooltip title={isCopied ? "Copiato!" : "Copia messaggio"}>
            <IconButton
              size="small"
              onClick={() => handleCopyMessage(params.row)}
              color={isCopied ? "success" : "default"}
            >
              {isCopied ? (
                <CheckIcon fontSize="small" />
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box sx={{ mx: "auto", width: "94%", maxWidth: 1600, pb: 5 }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, mt: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <ArticleIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Logs
          </Typography>
          {!isLoading && (
            <Chip
              label={`${rowCount} totali`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          )}
          {isFetching && !isLoading && (
            <CircularProgress size={16} sx={{ ml: 1 }} />
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          {autoRefresh ? (
            <PlayCircleOutlineIcon
              sx={{ fontSize: 18, color: "success.main" }}
            />
          ) : (
            <PauseCircleOutlineIcon
              sx={{ fontSize: 18, color: "text.disabled" }}
            />
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={500}
            sx={{ userSelect: "none" }}
          >
            Auto-refresh
          </Typography>
          <Switch
            size="small"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          <Tooltip title="Aggiorna ora">
            <IconButton size="small" onClick={handleRefresh} color="primary">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            component={RouterLink}
            to="/siapadmin/restored-logs"
            startIcon={<HistoryIcon />}
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Log ripristinati
          </Button>
        </Stack>
      </Stack>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          sx={{ gap: 1.5 }}
        >
          <FilterListIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            Filtri
          </Typography>

          <TextField
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Cerca nel messaggio..."
            sx={{ minWidth: 220 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />

          <ToggleButtonGroup
            size="small"
            exclusive
            value={sourceFilter}
            onChange={(e, val) => setSourceFilter(val)}
            sx={{ height: 32 }}
          >
            <ToggleButton
              value="client"
              sx={{ textTransform: "none", px: 1.5, fontSize: 12 }}
            >
              <ComputerIcon sx={{ fontSize: 14, mr: 0.5 }} /> Client
            </ToggleButton>
            <ToggleButton
              value="server"
              sx={{ textTransform: "none", px: 1.5, fontSize: 12 }}
            >
              <DnsIcon sx={{ fontSize: 14, mr: 0.5 }} /> Server
            </ToggleButton>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={levelFilter}
            onChange={(e, val) => setLevelFilter(val)}
            sx={{ height: 32 }}
          >
            <ToggleButton
              value="info"
              sx={{
                textTransform: "none",
                px: 1.5,
                fontSize: 12,
                color: "#2e7d32",
              }}
            >
              info
            </ToggleButton>
            <ToggleButton
              value="warn"
              sx={{
                textTransform: "none",
                px: 1.5,
                fontSize: 12,
                color: "#e65100",
              }}
            >
              warn
            </ToggleButton>
            <ToggleButton
              value="error"
              sx={{
                textTransform: "none",
                px: 1.5,
                fontSize: 12,
                color: "#c62828",
              }}
            >
              error
            </ToggleButton>
          </ToggleButtonGroup>

          <Autocomplete
            size="small"
            options={clientOptions}
            value={clientFilter}
            onChange={(e, val) => setClientFilter(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Client..."
                sx={{ minWidth: 180 }}
              />
            )}
            sx={{ minWidth: 180 }}
          />

          <TextField
            size="small"
            value={moduleFilter || ""}
            onChange={(e) => setModuleFilter(e.target.value || null)}
            placeholder="Modulo..."
            sx={{ minWidth: 150 }}
          />
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          sx={{ gap: 1.5, mt: 1 }}
        >
          <DateTimePicker
            label="Da"
            value={dateFrom}
            onChange={setDateFrom}
            ampm={false}
            slotProps={{
              textField: { size: "small", sx: { minWidth: 210 } },
              actionBar: { actions: ["clear", "accept"] },
            }}
            maxDateTime={dateTo || undefined}
          />

          <DateTimePicker
            label="A"
            value={dateTo}
            onChange={setDateTo}
            ampm={false}
            slotProps={{
              textField: { size: "small", sx: { minWidth: 210 } },
              actionBar: { actions: ["clear", "accept"] },
            }}
            minDateTime={dateFrom || undefined}
          />

          {hasActiveFilters && (
            <Chip
              label="Cancella filtri"
              size="small"
              variant="outlined"
              onDelete={handleClearFilters}
              onClick={handleClearFilters}
              sx={{ ml: 0.5 }}
            />
          )}

          <Box sx={{ flex: 1 }} />

          <Button
            variant="outlined"
            size="small"
            onClick={handleRefresh}
            startIcon={<RefreshIcon />}
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Aggiorna
          </Button>
        </Stack>
      </Paper>

      {/* DataGrid */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Box sx={{ width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row._uid}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            loading={isLoading}
            disableRowSelectionOnClick
            disableColumnMenu
            localeText={{
              noRowsLabel: "Nessun log trovato.",
              MuiTablePagination: {
                labelRowsPerPage: "Righe per pagina:",
                labelDisplayedRows: ({ from, to, count }) =>
                  `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`,
              },
            }}
            sx={dataGridSx}
          />
        </Box>
      </Paper>

      {/* Log Archives */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.5,
            cursor: "pointer",
            "&:hover": { backgroundColor: "action.hover" },
          }}
          onClick={() => setArchivesOpen((v) => !v)}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <ArchiveIcon color="primary" sx={{ fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Archivi Backup
            </Typography>
            {archives.length > 0 && (
              <Chip
                label={`${archives.length} archivi`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500, fontSize: 11 }}
              />
            )}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            {archivesOpen && (
              <>
                <Tooltip title="Ripristina archivi selezionati in restored_logs">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={restoreRunning ? <CircularProgress size={14} /> : <RestoreIcon />}
                      disabled={restoreRunning || selectedArchives.length === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestoreArchives();
                      }}
                      sx={{ textTransform: "none", fontWeight: 500, fontSize: 12 }}
                    >
                      Ripristina ({selectedArchives.length})
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Avvia backup manuale">
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={backupRunning ? <CircularProgress size={14} color="inherit" /> : <BackupIcon />}
                      disabled={backupRunning}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleManualBackup();
                      }}
                      sx={{ textTransform: "none", fontWeight: 500, fontSize: 12 }}
                    >
                      {backupRunning ? "Backup in corso..." : "Avvia Backup"}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Aggiorna lista">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      invalidateQuery("logArchives");
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {archivesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Stack>
        </Stack>

        <Collapse in={archivesOpen}>
          <Box sx={{ px: 2, pb: 2 }}>
            {archivesLoading ? (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : archives.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 2, textAlign: "center" }}
              >
                Nessun archivio disponibile.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          fontWeight: 600,
                          fontSize: 12,
                          color: "text.secondary",
                          borderBottom: "2px solid",
                          borderBottomColor: "divider",
                        },
                      }}
                    >
                      <TableCell padding="checkbox" />
                      <TableCell>Data Backup</TableCell>
                      <TableCell>File</TableCell>
                      <TableCell align="right">Log Totali</TableCell>
                      <TableCell align="right">Client</TableCell>
                      <TableCell align="right">Server</TableCell>
                      <TableCell align="right">Dimensione</TableCell>
                      <TableCell align="right">Periodo fino a</TableCell>
                      <TableCell align="center">Download</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedArchives.map((a, i) => {
                      const alreadyRestored = restoredFilenames.has(a.filename);
                      return (
                      <TableRow
                        key={a.filename || `${archivesPage}-${i}`}
                        sx={{
                          "&:last-child td": { borderBottom: 0 },
                          "&:hover": { backgroundColor: "action.hover" },
                          ...(alreadyRestored
                            ? { opacity: 0.65, backgroundColor: "action.selected" }
                            : {}),
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selectedArchives.includes(a.filename)}
                            disabled={alreadyRestored}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleArchiveSelection(a.filename)}
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                          {formatTimestamp(a.createdAt)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                          {a.filename}
                          {alreadyRestored && (
                            <Chip
                              label="Già ripristinato"
                              size="small"
                              sx={{ ml: 1, fontSize: 10, height: 20 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {a.totalLogs?.toLocaleString("it-IT")}
                        </TableCell>
                        <TableCell align="right">
                          {a.clientLogs?.toLocaleString("it-IT")}
                        </TableCell>
                        <TableCell align="right">
                          {a.serverLogs?.toLocaleString("it-IT")}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                          {formatBytes(a.sizeBytes)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                          {formatTimestamp(a.cutoffDate)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Scarica archivio">
                            <IconButton
                              size="small"
                              color="primary"
                              component="a"
                              href={`${window?._env_?.API_BASE_URL || "http://localhost:3000"}/log-archives/${a.filename}`}
                              download
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                    })}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={archives.length}
                  page={archivesPage}
                  onPageChange={(_, newPage) => setArchivesPage(newPage)}
                  rowsPerPage={archivesRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setArchivesRowsPerPage(parseInt(e.target.value, 10));
                    setArchivesPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Righe per pagina"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
                  }
                />
              </TableContainer>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              I log ripristinati sono consultabili dalla pagina Log ripristinati (pulsante in alto).
            </Typography>
          </Box>
        </Collapse>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const dataGridSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  "--DataGrid-containerBackground": "#004680",
  "& .MuiDataGrid-columnHeader": {
    color: "#fff",
    "&:focus, &:focus-within": { outline: "none" },
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 600,
    fontSize: 13,
    color: "#fff",
  },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    color: "#fff",
  },
  "& .MuiDataGrid-sortIcon": {
    color: "#fff",
  },
  "& .MuiDataGrid-iconButtonContainer .MuiIconButton-root": {
    color: "#fff",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid",
    borderBottomColor: "divider",
    color: "text.primary",
    display: "flex",
    alignItems: "center",
    "&:focus, &:focus-within": { outline: "none" },
  },
  "& .MuiDataGrid-row": {
    "&:hover": {
      backgroundColor: "action.hover",
    },
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid",
    borderTopColor: "divider",
    backgroundColor: "transparent",
  },
  "& .MuiDataGrid-columnSeparator": {
    display: "none",
  },
};
